import React from 'react';
import Icon from '../../../components/AppIcon';

const IconUsageRules = () => {
  return (
    <div className="space-y-6">
      {/* Approved Icon Colors */}
      <div className="p-6 rounded-lg bg-card border border-border">
        <h3 className="text-lg font-medium text-foreground mb-4">Approved Icon Colors</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-md bg-muted/30">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Zap" size={24} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">Primary (Teal)</span>
            </div>
            <p className="text-xs text-muted-foreground">Default for most icons on grey backgrounds</p>
            <code className="text-xs text-primary block mt-2">var(--color-primary) / #4FB3B8</code>
          </div>
          <div className="p-4 rounded-md bg-muted/30">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Star" size={24} color="var(--color-secondary)" />
              <span className="text-sm font-medium text-foreground">Secondary (Saffron)</span>
            </div>
            <p className="text-xs text-muted-foreground">For emphasis, categories, or secondary icons</p>
            <code className="text-xs text-primary block mt-2">var(--color-secondary) / #F5CB5C</code>
          </div>
          <div className="p-4 rounded-md bg-muted/30">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Info" size={24} color="var(--color-foreground)" />
              <span className="text-sm font-medium text-foreground">Alabaster (Text)</span>
            </div>
            <p className="text-xs text-muted-foreground">Neutral icons that should be clearly visible</p>
            <code className="text-xs text-primary block mt-2">var(--color-foreground) / #E8EDDF</code>
          </div>
        </div>
      </div>

      {/* Contrast Guidelines */}
      <div className="p-6 rounded-lg bg-card border border-border">
        <h3 className="text-lg font-medium text-foreground mb-4">Contrast Guidelines</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Always ensure sufficient contrast</p>
              <p className="text-xs text-muted-foreground">Icons must be clearly visible against their background at normal viewing distance</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Avoid grey icons on grey surfaces</p>
              <p className="text-xs text-muted-foreground">Low-saturation icons on dark/grey backgrounds create visibility issues</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Use brand colors for emphasis</p>
              <p className="text-xs text-muted-foreground">Primary teal and secondary saffron provide excellent contrast and maintain brand consistency</p>
            </div>
          </div>
        </div>
      </div>

      {/* Do / Don't Examples */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* DO */}
        <div className="p-6 rounded-lg bg-card border-2 border-success">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
            <h4 className="text-base font-medium text-foreground">DO</h4>
          </div>
          <div className="p-4 rounded-md bg-muted">
            <div className="flex items-center gap-3">
              <Icon name="Database" size={24} color="var(--color-primary)" />
              <span className="text-sm text-foreground">High contrast icon on grey surface</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Use primary teal for clear visibility on dark/grey backgrounds</p>
        </div>

        {/* DON'T */}
        <div className="p-6 rounded-lg bg-card border-2 border-destructive">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="XCircle" size={20} color="var(--color-destructive)" />
            <h4 className="text-base font-medium text-foreground">DON'T</h4>
          </div>
          <div className="p-4 rounded-md bg-muted">
            <div className="flex items-center gap-3">
              <Icon name="Database" size={24} color="#6B7280" />
              <span className="text-sm text-foreground">Low contrast grey icon</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Avoid grey or muted colors that blend into the background</p>
        </div>
      </div>

      {/* Icon Sizing */}
      <div className="p-6 rounded-lg bg-card border border-border">
        <h3 className="text-lg font-medium text-foreground mb-4">Icon Sizing Conventions</h3>
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Icon name="Zap" size={16} color="var(--color-primary)" />
            <span className="text-xs text-muted-foreground">16px - Small / Inline</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Zap" size={20} color="var(--color-primary)" />
            <span className="text-xs text-muted-foreground">20px - Default</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Zap" size={24} color="var(--color-primary)" />
            <span className="text-xs text-muted-foreground">24px - Large / Featured</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Zap" size={32} color="var(--color-primary)" />
            <span className="text-xs text-muted-foreground">32px - Hero / Emphasis</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IconUsageRules;